import type { StringKey } from "../contract/strings";
import { retryAfterMinutes } from "./session-errors";

/**
 * One meeting at a time (#729, #735): a start or a join refused because another meeting
 * is still live, and what Home offers to do about it.
 *
 * Two refusals carry the other meeting's id, and each says something different about it:
 *
 * - `billing_meeting_in_progress` (create only): this account already hosts a live
 *   meeting. `device.is_this_device` says whether it is running here or on another of the
 *   account's devices. The account pays for it, so End it is always offered.
 * - `device_in_live_meeting` (create and join): this device is still in another live
 *   meeting. `can_end` says whether this account pays for it: End it if so, Leave it if
 *   not.
 *
 * A refusal without a `session_id` -- an older server -- is not one of these: Home keeps
 * the plain sentence `session-errors` gives it.
 */
export type OtherMeeting =
  | { kind: "billing"; sessionId: string; here: boolean }
  | { kind: "device"; sessionId: string; canEnd: boolean };

export type OtherMeetingAction = "end" | "leave";

/** The states in which a meeting still blocks a start or a join (the server's `LIVE_STATES`). */
const LIVE_STATES: ReadonlySet<string> = new Set(["open", "recording", "closing"]);

/**
 * How long after End before the wait says it may take minutes. A host device that is
 * alive finishes within seconds; one that is not keeps the meeting `closing` until the
 * server's close grace runs out.
 */
export const ENDING_SLOW_AFTER_MS = 15_000;
/**
 * How long to wait for an ended meeting to stop blocking before giving up. A host device
 * that never answers holds it for the close grace (90 s), then the closing sweep anchors
 * the stop barrier and waits its deadline (120 s) on top, each on the worker's tick: about
 * four and a half minutes at worst, so six leaves room without waiting forever.
 */
export const ENDING_GIVE_UP_MS = 6 * 60_000;
/** How often to ask whether the ended meeting still blocks. */
export const ENDING_POLL_MS = 3_000;

/** The other meeting a create or a join was refused over, or null for any other failure. */
export function otherMeetingRefusal(error: unknown): OtherMeeting | null {
  const data = (error as { data?: Record<string, unknown> | null } | null)?.data;
  const sessionId = data?.session_id;
  if (typeof sessionId !== "string" || sessionId === "") return null;
  if (data?.code === "billing_meeting_in_progress") {
    const device = data.device as { is_this_device?: unknown } | null | undefined;
    return { kind: "billing", sessionId, here: device?.is_this_device === true };
  }
  if (data?.code === "device_in_live_meeting") {
    return { kind: "device", sessionId, canEnd: data.can_end === true };
  }
  return null;
}

/** The sentence that says what is still running, and where. */
export function otherMeetingMessage(meeting: OtherMeeting): StringKey {
  if (meeting.kind === "device") return "home.device_in_meeting";
  return meeting.here ? "home.other_meeting_here" : "home.other_meeting_elsewhere";
}

/** End it for a meeting this account pays for; Leave it for anyone else's. */
export function otherMeetingAction(meeting: OtherMeeting): OtherMeetingAction {
  return meeting.kind === "billing" || meeting.canEnd ? "end" : "leave";
}

/** A failure to end or leave, as the copy Home puts on its status line. */
export class OtherMeetingError extends Error {
  constructor(
    readonly key: StringKey,
    readonly vars: Record<string, string | number> = {}
  ) {
    super(key);
    this.name = "OtherMeetingError";
  }
}

/** What a refused End says: not this account's to end, ended too often, or a connection fault. */
export function endFailure(error: unknown): OtherMeetingError {
  const failure = error as { status?: unknown; retryAfter?: unknown } | null;
  if (failure?.status === 403) return new OtherMeetingError("home.error_end_not_yours");
  const retryAfter = failure?.retryAfter;
  if (
    failure?.status === 429 &&
    typeof retryAfter === "number" &&
    Number.isFinite(retryAfter) &&
    retryAfter > 0
  ) {
    return new OtherMeetingError("home.error_end_limited", { count: retryAfterMinutes(retryAfter) });
  }
  return new OtherMeetingError("home.error_end");
}

/** The calls the wait makes, injected so the tests need no network and no clock. */
export interface OtherMeetingPorts {
  endSession: (id: string) => Promise<{ state?: string } | null | undefined>;
  /**
   * Leave the other meeting, closing any claims this device's earlier page left open
   * (#742): that page may have died mid-meeting, and this one cannot finish them.
   */
  leaveSession: (id: string) => Promise<unknown>;
  getSession: (id: string) => Promise<{ state?: string } | null | undefined>;
  wallet: () => Promise<{ start_refusal?: string | null } | null | undefined>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
}

export interface ResolveOptions {
  signal?: AbortSignal;
  /** Called once, when the wait passes `ENDING_SLOW_AFTER_MS`. */
  onSlow?: () => void;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("other_meeting_wait_cancelled", "AbortError");
}

/**
 * End or leave the other meeting, and return once it no longer stands in the way.
 *
 * **Leave it** is one call: this device's membership ends, and with it the refusal.
 *
 * **End it** moves the meeting to `closing`, as its host's own End does. A meeting the
 * server answers as already over (`finalising`, `complete`, `failed`: a false start ends
 * at once) needs no wait. Otherwise it blocks until it reaches `finalising`, which takes
 * as long as its host device does to finish -- seconds when that device is alive, the
 * server's close grace when it is not. So this waits, asking the one thing this device
 * can read:
 *
 * - for a meeting on another device, the wallet's `start_refusal`, since this device is
 *   not in that meeting and cannot read it;
 * - for a meeting this device is in, the meeting itself. It first tries to leave it, which
 *   frees this device at once, closing any claims its earlier page left open (#742); the
 *   server refuses that while another device's final watermark is still due, and then the
 *   meeting's own end frees it.
 *
 * Never retries the create itself to find out: that is rate-limited per device.
 *
 * Throws `OtherMeetingError` with the copy to show, or an `AbortError` when `signal` fires.
 */
export async function resolveOtherMeeting(
  meeting: OtherMeeting,
  action: OtherMeetingAction,
  ports: OtherMeetingPorts,
  { signal, onSlow }: ResolveOptions = {}
): Promise<void> {
  const id = meeting.sessionId;
  if (action === "leave") {
    try {
      await ports.leaveSession(id);
    } catch {
      throwIfAborted(signal);
      throw new OtherMeetingError("home.error_leave");
    }
    throwIfAborted(signal);
    return;
  }

  let ended: { state?: string } | null | undefined;
  try {
    ended = await ports.endSession(id);
  } catch (err) {
    throwIfAborted(signal);
    throw endFailure(err);
  }
  throwIfAborted(signal);
  if (ended?.state !== undefined && !LIVE_STATES.has(ended.state)) return;

  if (meeting.kind === "device") {
    try {
      await ports.leaveSession(id);
      throwIfAborted(signal);
      return;
    } catch {
      // Refused while another device, such as the host's, still owes its final watermark:
      // the meeting's own end frees this device instead.
      throwIfAborted(signal);
    }
  }

  const started = ports.now();
  let saidSlow = false;
  for (;;) {
    await ports.sleep(ENDING_POLL_MS);
    throwIfAborted(signal);
    const waited = ports.now() - started;
    if (!saidSlow && waited >= ENDING_SLOW_AFTER_MS) {
      saidSlow = true;
      onSlow?.();
    }
    try {
      if (meeting.kind === "billing") {
        const wallet = await ports.wallet();
        if (wallet && wallet.start_refusal !== "billing_meeting_in_progress") return;
      } else {
        const session = await ports.getSession(id);
        if (session?.state !== undefined && !LIVE_STATES.has(session.state)) return;
      }
    } catch {
      // One failed read is not an answer. The next tick asks again, and the deadline
      // below still bounds the wait.
    }
    throwIfAborted(signal);
    if (ports.now() - started >= ENDING_GIVE_UP_MS) throw new OtherMeetingError("home.error_end_slow");
  }
}
